import {
  fileOpErrorsResultSchema,
  purgeFileOpErrorsResultSchema,
  reconciliationExtensionsResultSchema,
  scanReconciliationSchema,
  type FileOpErrorsResult,
  type FileOpErrorSeverity,
  type JobType,
  type ReconciliationExtensionsResult,
  type ReconciliationExportReason,
  type ScanReconciliation,
} from '@xphoto/shared';
import { apiFetch } from '../lib/http';
import { AdminApiError } from './admin-api';

/**
 * Client typé du JOURNAL DES ERREURS DE TRAITEMENT DE FICHIERS (Lot C). Comme les
 * autres clients du front, chaque réponse est RE-PARSÉE via son schéma partagé
 * `@xphoto/shared`. Réutilise `AdminApiError` (statut + code) du client admin.
 *
 * Endpoints PAR BIBLIOTHÈQUE (la `libraryId` vient de l'utilisateur connecté) :
 * - `GET    /api/libraries/:id/file-errors?limit=&offset=&userId=&severity=&jobType=`
 *   → `{ errors, total }` (accès LECTURE).
 * - `DELETE /api/libraries/:id/file-errors` body `{ before? }` → `{ deleted }`
 *   (accès ÉCRITURE → propriétaire/superadmin ; 403 sinon).
 */

async function errorCodeOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { code?: string } };
    if (typeof body?.error?.code === 'string') return body.error.code;
  } catch {
    // Corps non-JSON : code générique.
  }
  return 'UNKNOWN';
}

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) throw new AdminApiError(res.status, await errorCodeOf(res));
  return res.json();
}

/** Filtres optionnels du listing. `jobTypes` (groupe) → CSV côté requête. */
export interface FileErrorsFilters {
  userId?: string | undefined;
  severity?: FileOpErrorSeverity | undefined;
  jobTypes?: readonly JobType[] | undefined;
}

/** `GET /api/libraries/:id/file-errors` — page filtrée `{ errors, total }`. */
export async function fetchFileErrors(
  libraryId: string,
  limit: number,
  offset: number,
  filters: FileErrorsFilters = {},
  signal?: AbortSignal,
): Promise<FileOpErrorsResult> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (filters.userId !== undefined) params.set('userId', filters.userId);
  if (filters.severity !== undefined) params.set('severity', filters.severity);
  if (filters.jobTypes !== undefined && filters.jobTypes.length > 0) {
    params.set('jobType', filters.jobTypes.join(','));
  }
  const res = await apiFetch(`/api/libraries/${libraryId}/file-errors?${params.toString()}`, {
    credentials: 'same-origin',
    signal: signal ?? null,
  });
  return fileOpErrorsResultSchema.parse(await readJson(res));
}

/**
 * `DELETE /api/libraries/:id/file-errors` — purge le journal. Sans `before` → tout ;
 * avec `before` (ISO 8601) → les entrées `ts < before`. Renvoie le nombre supprimé.
 */
export async function purgeFileErrors(libraryId: string, before?: string): Promise<number> {
  const res = await apiFetch(`/api/libraries/${libraryId}/file-errors`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(before !== undefined ? { before } : {}),
  });
  return purgeFileOpErrorsResultSchema.parse(await readJson(res)).deleted;
}

/**
 * `GET /api/libraries/:id/scan-reconciliation` — réconciliation disque↔index du
 * DERNIER scan terminé (accès LECTURE). `counts` est `null` si aucun scan n'a
 * encore abouti (ou si son résultat est antérieur à l'ajout de la réconciliation).
 */
export async function fetchScanReconciliation(
  libraryId: string,
  signal?: AbortSignal,
): Promise<ScanReconciliation> {
  const res = await apiFetch(`/api/libraries/${libraryId}/scan-reconciliation`, {
    credentials: 'same-origin',
    signal: signal ?? null,
  });
  return scanReconciliationSchema.parse(await readJson(res));
}

/**
 * `GET /api/libraries/:id/reconciliation/extensions` — ventilation des extensions
 * refusées du dernier scan terminé (accès LECTURE). Liste vide si aucun scan.
 */
export async function fetchReconciliationExtensions(
  libraryId: string,
  signal?: AbortSignal,
): Promise<ReconciliationExtensionsResult> {
  const res = await apiFetch(`/api/libraries/${libraryId}/reconciliation/extensions`, {
    credentials: 'same-origin',
    signal: signal ?? null,
  });
  return reconciliationExtensionsResultSchema.parse(await readJson(res));
}

/**
 * URL (RELATIVE, sans slot) de l'export TXT des fichiers ignorés du dernier scan
 * terminé, pour un `<a download href={withSlotParam(url)}>` (téléchargement natif,
 * cookie same-origin). `withSlotParam` (appelé par l'appelant) y ajoute le slot actif.
 */
export function skippedExportUrl(
  libraryId: string,
  reason: ReconciliationExportReason = 'all',
): string {
  return `/api/libraries/${libraryId}/reconciliation/skipped-export?reason=${reason}`;
}
